import { useState } from 'react';
import {
  JobCountQuery,
  getJobCount,
} from '@doda/common/src/services/getJobCountAPI';
import { useSearchKeyword } from 'components/SearchSideBar/SearchKeyword/hooks/useSearchKeyword';
import { useEmploymentStatus } from 'components/SearchSideBar/SearchEmploymentStatus/hooks/useEmploymentStatus';
import { useSearchIncome } from 'components/SearchSideBar/SearchIncome/hooks/useSearchIncome';
import { useHolidayAndWayOfWorking } from 'components/SearchSideBar/SearchDetailCondition/hooks/useHolidayAndWayOfWorking';
import { useRequirements } from 'components/SearchSideBar/SearchDetailCondition/hooks/useRequirements';
import { useWelfareBenefits } from 'components/SearchSideBar/SearchDetailCondition/hooks/useWelfareBenefits';
import { useLanguageSkillList } from 'components/SearchSideBar/SearchDetailCondition/hooks/useLanguageSkillList';
import { usePositions } from 'components/SearchSideBar/SearchDetailCondition/hooks/usePositions';
import { useEmployeeAverageAge } from 'components/SearchSideBar/SearchDetailCondition/hooks/useEmployeeAverageAge';
import { useEmployeeNum } from 'components/SearchSideBar/SearchDetailCondition/hooks/useEmployeeNum';
import { useEstablish } from 'components/SearchSideBar/SearchDetailCondition/hooks/useEstablish';
import { usePickUpCondition } from 'components/SearchSideBar/SearchPickUpCondition/hooks/usePickUpCondition';
import { removeUndefinedFromObject } from '@doda/common/src/utils/objectUtil';
import {
  JobCountResponse,
  SavedSearchResponse,
} from '@doda/api-client/aspida/@types';
import { useWorkingPlaceEnvironment } from 'components/SearchSideBar/SearchDetailCondition/hooks/useWorkingPlaceEnvironment';
import {
  getInitialExcludeKeywordCategory,
  getInitialExcludeKeywordText,
  getInitialIncludeKeywordCategory,
  getInitialIncludeKeywordText,
} from 'components/SearchSideBar/SearchKeyword/logics/getInitialData';
import { InitialSideBarMasterDataType } from 'components/SearchSideBar/BasicInfo/types';
import { useJobOfferKind } from 'components/SearchSideBar/SearchDetailCondition/hooks/useJobOfferKind';
import { useSelector } from 'react-redux';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { SAVED_CONDITION_LIMIT } from 'constants/searchCondition';
import { useOccupation } from '../../BasicInfo/OccupationArea/hooks/useOccupation';
import { useLocation } from '../../BasicInfo/LocationArea/hooks/useLocation';
import { useIndustry } from '../../BasicInfo/IndustryArea/hooks/useIndustry';
import { useRouteStation } from '../../BasicInfo/LocationArea/RouteStation/hooks/useRouteStation';
import { DrillDownTypes, FeaturedSearchConditionQuery } from '../../types';
import { selectedCurrentConditionCount } from '../../../../logics/selectedCurrentConditionCount';

interface ArgumentType {
  initialSideBarMasterData: InitialSideBarMasterDataType;
  savedSearchCondition?: SavedSearchResponse | undefined;
  initialJobCountData: JobCountResponse;
  featuredSearchCondition?: FeaturedSearchConditionQuery;
  initialDrillDown?: DrillDownTypes;
}

/**
 * サイドバーの検索条件をまとめたhooks
 * @param param.initialSideBarMasterData 表示用に整えられたマスタデータ
 * @param param.savedSearchCondition 保存した検索条件
 * @param param.initialJobCountData 初期表示時の求人件数
 * @param param.featuredSearchCondition 特集枠の検索条件
 * @param param.initialDrillDown ドリルダウンのデータ
 * @returns 検索条件の表示、更新オブジェクト
 */
export const useSearchCondition = ({
  initialSideBarMasterData,
  savedSearchCondition,
  initialJobCountData,
  featuredSearchCondition,
  initialDrillDown,
}: ArgumentType) => {
  const { ha, k, kwc, ni, niwc, op, es, ne, tp, qc } = useSelector(
    appQueryParamsSelector
  );

  const commonArgs = {
    savedSearchCondition,
    featuredSearchCondition,
    jobCountData: initialJobCountData,
  };
  const detailsHooksArgs = {
    ...commonArgs,
    queries: op,
  };

  const occupation = useOccupation({
    initialOccupationData: initialSideBarMasterData?.occupation,
    initialOccupationDrillDown: initialDrillDown?.occupation,
  });

  const location = useLocation({
    initialLocationData: initialSideBarMasterData?.location,
    initialLocationDrillDown: initialDrillDown?.location,
  });

  const routeStation = useRouteStation({
    initialRouteStationData: initialSideBarMasterData?.routeStation,
    initialRouteStationDrillDown: initialDrillDown?.routeStation ?? [],
  });

  const industry = useIndustry({
    initialIndustryData: initialSideBarMasterData?.industry,
    initialDrillDown: initialDrillDown?.industry,
  });

  const income = useSearchIncome({
    savedSearchCondition,
    featuredSearchCondition,
    haQuery: ha,
  });

  const includeSearchKeyword = useSearchKeyword({
    defaultValue: getInitialIncludeKeywordText({
      savedSearchCondition,
      featuredSearchCondition,
      keyWordQuery: k,
      categoryQuery: kwc,
    }),
    defaultCategory: getInitialIncludeKeywordCategory({
      savedSearchCondition,
      featuredSearchCondition,
      keyWordQuery: k,
      categoryQuery: kwc,
    }),
  });

  const excludeSearchKeyword = useSearchKeyword({
    defaultValue: getInitialExcludeKeywordText({
      savedSearchCondition,
      featuredSearchCondition,
      keyWordQuery: ni,
      categoryQuery: niwc,
    }),
    defaultCategory: getInitialExcludeKeywordCategory({
      savedSearchCondition,
      featuredSearchCondition,
      keyWordQuery: ni,
      categoryQuery: niwc,
    }),
  });

  const holidayAndWayOfWorking = useHolidayAndWayOfWorking(detailsHooksArgs);
  const requirements = useRequirements(detailsHooksArgs);
  const workingPlaceEnvironment = useWorkingPlaceEnvironment(detailsHooksArgs);
  const welfareBenefits = useWelfareBenefits(detailsHooksArgs);
  const languageSkill = useLanguageSkillList(detailsHooksArgs);
  const positions = usePositions(detailsHooksArgs);
  const employeeAverageAge = useEmployeeAverageAge(detailsHooksArgs);
  const employmentStatus = useEmploymentStatus(detailsHooksArgs);
  const employeeNum = useEmployeeNum({
    ...commonArgs,
    queries: ne,
  });
  const establish = useEstablish({
    ...commonArgs,
    queries: es,
  });
  const jobOfferKind = useJobOfferKind({
    ...commonArgs,
    query: tp,
  });
  const pickUpCondition = usePickUpCondition({
    ...commonArgs,
    holidayAndWayOfWorkingList:
      holidayAndWayOfWorking.holidayAndWayOfWorkingList,
    requirementList: requirements.requirementList,
  });

  const [jobCountData, setJobCountData] = useState(initialJobCountData);
  const [modalTotalCount, setModalTotalCount] = useState(
    initialJobCountData.totalCount ?? 0
  );
  const totalCount = jobCountData.totalCount ?? 0;

  const [isConditionUpperLimitError, setIsConditionUpperLimitError] =
    useState(true);

  const selectedCurrentCondition = {
    jid: featuredSearchCondition?.jid,
    searchJobTypeLId: occupation.selectedOccupationLIdList,
    searchJobTypeMId: occupation.selectedOccupationMIdList,
    searchJobTypeSId: occupation.selectedOccupationSIdList,
    areaId: location.selectedAreaIdList,
    prefectureId: location.selectedPrefectureIdList,
    cityId: location.selectedCityIdList,
    wardId: location.selectedWardIdList,
    routeId: routeStation.selectedRouteIdList,
    stationId: routeStation.selectedStationIdList,
    industryLId: industry.selectedIndustryLIdList,
    industrySId: industry.selectedIndustrySIdList,
    employmentStatus: employmentStatus.selectedEmploymentStatusIdList,
    lowerLimitIncome: income.selectedLowerIncome,
    upperLimitIncome: income.selectedUpperIncome,
    isNewArrival: pickUpCondition.isNewArrival,
    isNearDeadline: pickUpCondition.isNearDeadline,
    includedKeywordCategory: includeSearchKeyword.state.searchText
      ? includeSearchKeyword.state.searchCategory
      : undefined,
    excludedKeywordCategory: excludeSearchKeyword.state.searchText
      ? excludeSearchKeyword.state.searchCategory
      : undefined,
    includedKeyword: includeSearchKeyword.state.searchText
      ? includeSearchKeyword.state.searchText.split(/\s+/)
      : [],
    excludedKeyword: excludeSearchKeyword.state.searchText
      ? excludeSearchKeyword.state.searchText.split(/\s+/)
      : [],
    holidayAndWayOfWorking:
      holidayAndWayOfWorking.selectedHolidayAndWayOfWorkingList,
    requirements: requirements.selectedRequirementList,
    workplaceEnvironment:
      workingPlaceEnvironment.selectedWorkingPlaceEnvironmentList,
    welfareBenefits: welfareBenefits.selectedWelfareBenefitsList,
    languageSkills: languageSkill.selectedLanguageSkillList,
    positions: positions.selectedPositionList,
    employeeAverageAge: employeeAverageAge.selectedEmployeeAverageAgeList,
    employNum: employeeNum.selectedEmployNumList,
    establish: establish.selectedEstablishList,
    jobOfferKind: jobOfferKind.selectedJobOfferKindList,
    qualificationId: qc,
  };

  const updateJobCount = async (
    currentCondition?: JobCountQuery,
    isToggleOccupation?: boolean,
    isToggleIndustry?: boolean
  ) => {
    const conditions: typeof selectedCurrentCondition = {
      ...selectedCurrentCondition,
      ...currentCondition,
    };

    setIsConditionUpperLimitError(
      selectedCurrentConditionCount(conditions) <= SAVED_CONDITION_LIMIT
    );

    const res = await getJobCount({
      ...removeUndefinedFromObject(conditions),
    });

    setJobCountData(res);
    occupation.updateOccupationJobCount(
      res.searchJobTypeLList,
      res.searchJobTypeMList,
      res.searchJobTypeSList,
      isToggleOccupation ?? false
    );
    location.updateLocationJobCount(
      res.areaList,
      res.prefectureList,
      res.cityList,
      res.wardList
    );
    routeStation.updateRouteStationJobCount(res.routeList, res.stationList);
    industry.updateIndustryJobCount(
      res.industryLList,
      res.industrySList,
      isToggleIndustry ?? false
    );
    employmentStatus.updateEmploymentStatusListJobCount(
      res.employmentStatusList
    );
    holidayAndWayOfWorking.updateHolidayAndWayOfWorkingListJobCount(
      res.holidayAndWayOfWorkingList
    );
    requirements.updateRequirementListJobCount(res.requirementsList);
    workingPlaceEnvironment.updateWorkingPlaceEnvironmentJobCount(
      res.workplaceEnvironmentList
    );
    welfareBenefits.updateWelfareBenefitsListJobCount(res.welfareBenefitsList);
    languageSkill.updateLanguageListJobCount(res.languageSkillsList);
    positions.updatePositionListJobCount(res.positionsList);
    employeeAverageAge.updateEmployeeAverageAgeListJobCount(
      res.employeeAverageAgeList
    );
    employeeNum.updateEmployNumListJobCount(res.employNumList);
    establish.updateEstablishListJobCount(res.establishList);
    jobOfferKind.updateJobOfferKindListJobCount(res.jobOfferKindList);
    pickUpCondition.updateNewArrivalJobCount(res.newArrivalCount);
    pickUpCondition.updateNearDeadlineJobCount(res.nearDeadlineCount);
  };

  /**
   * モーダルに渡す件数更新処理
   * @param currentCondition
   */
  const updateModalJobCount = async (currentCondition?: JobCountQuery) => {
    const conditions = {
      ...selectedCurrentCondition,
      ...currentCondition,
    };

    setIsConditionUpperLimitError(
      selectedCurrentConditionCount(conditions) <= SAVED_CONDITION_LIMIT
    );

    const res = await getJobCount(removeUndefinedFromObject(conditions));

    setModalTotalCount(res.totalCount ?? 0);
  };

  return {
    occupation: {
      occupationDisplayList: occupation.occupationDisplayList,
      occupationDrillDown: occupation.occupationDrillDown,
      updateIsCheckedOccupationLList: occupation.updateIsCheckedOccupationLList,
      updateIsCheckedOccupationMList: occupation.updateIsCheckedOccupationMList,
      updateIsCheckedOccupationSList: occupation.updateIsCheckedOccupationSList,
    },
    location: {
      locationDisplayList: location.locationDisplayList,
      locationDrillDown: location.locationDrillDown,
      updateIsCheckedAreaList: location.updateIsCheckedAreaList,
      updateIsCheckedPrefectureList: location.updateIsCheckedPrefectureList,
      updateIsCheckedCityList: location.updateIsCheckedCityList,
      updateIsCheckedWardList: location.updateIsCheckedWardList,
      updateLocationDrillDownIsChecked:
        location.updateLocationDrillDownIsChecked,
    },
    routeStation: {
      routeStationDisplayList: routeStation.routeStationDisplayList,
      routeStationDrillDown: routeStation.routeStationDrillDown,
      updateIsCheckedRouteList: routeStation.updateIsCheckedRouteList,
      updateIsCheckedStationList: routeStation.updateIsCheckedStationList,
      updateStationDrillDownIsChecked:
        routeStation.updateStationDrillDownIsChecked,
    },
    industry: {
      industryDisplayList: industry.industryDisplayList,
      industryDrillDown: industry.industryDrillDown,
      updateIsCheckedIndustryLList: industry.updateIsCheckedIndustryLList,
      updateIsCheckedIndustrySList: industry.updateIsCheckedIndustrySList,
      updateDrillDownIsChecked: industry.updateDrillDownIsChecked,
    },
    employmentStatus: {
      employmentStatusList: employmentStatus.employmentStatusList,
      updateIsCheckEmploymentStatusList:
        employmentStatus.updateIsCheckEmploymentStatusList,
    },
    income: {
      upperIncome: income.upperIncome,
      lowerIncome: income.lowerIncome,
      isIncomeRangeError: income.isIncomeRangeError,
      updateUpperIncome: income.updateUpperIncome,
      updateLowerIncome: income.updateLowerIncome,
    },
    pickUpCondition: {
      pickUpConditionDisplayList: pickUpCondition.pickUpConditionDisplayList,
      updateIsCheckedIsNewArrival: pickUpCondition.updateIsCheckedIsNewArrival,
      updateIsCheckedIsNearDeadline:
        pickUpCondition.updateIsCheckedIsNearDeadline,
    },
    searchDetail: {
      holidayAndWayOfWorking: {
        holidayAndWayOfWorkingList:
          holidayAndWayOfWorking.holidayAndWayOfWorkingList,
        updateIsCheckedHolidayAndWayOfWorkingList:
          holidayAndWayOfWorking.updateIsCheckedHolidayAndWayOfWorkingList,
      },
      requirements: {
        requirementList: requirements.requirementList,
        updateIsCheckedRequirementList:
          requirements.updateIsCheckedRequirementList,
      },
      workingPlaceEnvironment: {
        workingPlaceEnvironmentList:
          workingPlaceEnvironment.workingPlaceEnvironmentList,
        updateIsCheckedWorkingPlaceEnvironmentList:
          workingPlaceEnvironment.updateIsCheckedWorkingPlaceEnvironmentList,
      },
      welfareBenefits: {
        welfareBenefitsList: welfareBenefits.welfareBenefitsList,
        updateIsCheckedWelfareBenefitsList:
          welfareBenefits.updateIsCheckedWelfareBenefitsList,
      },
      languageSkill: {
        languageSkillList: languageSkill.languageSkillList,
        updateIsCheckedLanguageSkillList:
          languageSkill.updateIsCheckedLanguageSkillList,
      },
      positions: {
        positionList: positions.positionList,
        updateIsCheckedPositionList: positions.updateIsCheckedPositionList,
      },
      employeeAverageAge: {
        employeeAverageAgeList: employeeAverageAge.employeeAverageAgeList,
        updateIsCheckedEmployeeAverageAgeList:
          employeeAverageAge.updateIsCheckedEmployeeAverageAgeList,
      },
      employNum: {
        employNumList: employeeNum.employNumList,
        updateIsCheckedEmployNumList: employeeNum.updateIsCheckedEmployNumList,
      },
      establish: {
        establishList: establish.establishList,
        updateIsCheckedEstablishList: establish.updateIsCheckedEstablishList,
      },
      jobOfferKind: {
        jobOfferKindList: jobOfferKind.jobOfferKindList,
        updateIsCheckedJobOfferKindList:
          jobOfferKind.updateIsCheckedJobOfferKindList,
      },
    },

    includeSearchKeyword,
    excludeSearchKeyword,
    selectedCurrentCondition,
    updateJobCount,
    updateModalJobCount,
    totalCount,
    modalTotalCount,
    jobCountData,
    isConditionUpperLimitError,
  };
};
